/**
 * The ride dispatch wave policy as a GOVERNED setting (W8-GOVERNED-DISPATCH-MONEY, D444, ADR-0076; findings C-02, D-43,
 * D-44; owner decisions D435 §5 #14 and #15 — the recommended defaults accepted).
 *
 * Before D444 every search used the api's constants (`DEFAULT_WAVE_POLICY`: 3 km first radius, +2 km per wave up to 9 km,
 * 5 drivers per wave, a 30 s offer, 4 waves, 120 s in total; `DEFAULT_RANKING_CONFIG`: the balanced weights), the
 * request-group lanes a second, divergent literal (1.5/1.5/6 km), a driver who ignored every offer was never paused, and a
 * driver's cancel after accepting ended the ride. `dispatch.wavePolicy` is a settings-kernel key (D393, ADR-0060): country
 * and market scope, OVERRIDABLE, four eyes on every change, versioned, effective-dated, content-hashed. One value carries:
 *
 *  - `wave` — the search: first radius, radius increment, maximum radius (meters), drivers per wave, offer TTL, wave
 *    count, the search's time budget and the pause between empty searches (seconds);
 *  - `ranking` — the weights of the ranking factors and their reference scales (seconds, meters, rating maximum);
 *  - `autoPause.ignoredOffersInARow` — after this many offers in a row that a driver let lapse without answering, the
 *    driver stops receiving offers until he resumes (D-43; the owner's 3; 0 = never);
 *  - `driverCancel.redispatchLimit` — how many times a ride whose driver cancelled after accepting is dispatched again
 *    automatically, keeping the rider's request (D-44; the owner's 1; 0 = the ride ends, as before).
 *
 * Defaults: an UNSET market resolves the pre-D444 constants with the auto-pause and the re-dispatch OFF
 * ({@link DEFAULT_DISPATCH_WAVE_POLICY}, reason `DISPATCH_WAVE_POLICY_DEFAULT`), so publishing nothing changes no market; a
 * version the reader cannot parse falls back to the same default with `DISPATCH_WAVE_POLICY_INVALID` (a search never stops
 * over a policy it cannot read). Every decision carries its reason code and policy version (`dispatch.wavePolicy@…#v1`),
 * which the dispatch run, the wave's provenance and each pause / re-dispatch record.
 *
 * Pure and deterministic. Neutral: no country or market is named; distances are meters, durations seconds.
 */
import { SCOPE_REF_KIND, marketSettingContext, policySetting, resolvePolicy, settingScopeRefIn, settingScopeRefValid, settingSnapshotFields } from '@vello/config';
export const DISPATCH_WAVE_POLICY_KEY = 'dispatch.wavePolicy';
/** The search as it ran before D444 (the api's `DEFAULT_WAVE_POLICY` + the auto-dispatch 10 s retry). */
export const PRE_D444_WAVE_SEARCH = {
    initialRadiusMeters: 3_000,
    radiusIncrementMeters: 2_000,
    maxRadiusMeters: 9_000,
    batchSize: 5,
    offerTtlSeconds: 30,
    maxWaves: 4,
    maxTotalSeconds: 120,
    retryIntervalSeconds: 10,
};
/** The ranking as it ran before D444 (`presetWeights('balanced')` + the api's reference scales). */
export const PRE_D444_RANKING = {
    weightsBasisPoints: { eta: 2500, pickup: 2500, acceptance: 2000, idle: 1500, destinationAlignment: 500, rating: 1000 },
    etaRefSeconds: 900,
    pickupRefMeters: 5_000,
    idleRefSeconds: 1_800,
    ratingMax: 5,
};
/** The policy of an UNSET market: the pre-D444 search and ranking, no auto-pause, no automatic re-dispatch. */
export const DEFAULT_DISPATCH_WAVE_POLICY = {
    wave: PRE_D444_WAVE_SEARCH,
    ranking: PRE_D444_RANKING,
    autoPause: { ignoredOffersInARow: 0 },
    driverCancel: { redispatchLimit: 0 },
};
/** Bounds of each governed number (the database predicate of m0147 mirrors them). */
export const DISPATCH_WAVE_BOUNDS = {
    initialRadiusMeters: [100, 100_000],
    radiusIncrementMeters: [0, 50_000],
    maxRadiusMeters: [100, 200_000],
    batchSize: [1, 50],
    offerTtlSeconds: [10, 300],
    maxWaves: [1, 20],
    maxTotalSeconds: [30, 3_600],
    retryIntervalSeconds: [2, 120],
    etaRefSeconds: [60, 7_200],
    pickupRefMeters: [100, 100_000],
    idleRefSeconds: [60, 86_400],
    ratingMax: [1, 10],
    weightBasisPoints: [0, 10_000],
    ignoredOffersInARow: [0, 20],
    redispatchLimit: [0, 3],
    /** D856 (R4): one pause window, in seconds (30 s ... 24 h), and how many steps of the escalation list. */
    pauseDurationSeconds: [30, 86_400],
    pauseEscalationSteps: [1, 10],
};
export const DISPATCH_WAVE_POLICY_REASON_CODES = ['DISPATCH_WAVE_POLICY_GOVERNED', 'DISPATCH_WAVE_POLICY_DEFAULT', 'DISPATCH_WAVE_POLICY_INVALID'];
const MARKET_ID = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/;
const WAVE_KEYS = ['initialRadiusMeters', 'radiusIncrementMeters', 'maxRadiusMeters', 'batchSize', 'offerTtlSeconds', 'maxWaves', 'maxTotalSeconds', 'retryIntervalSeconds'];
const RANKING_KEYS = ['weightsBasisPoints', 'etaRefSeconds', 'pickupRefMeters', 'idleRefSeconds', 'ratingMax'];
const WEIGHT_KEYS = ['eta', 'pickup', 'acceptance', 'idle', 'destinationAlignment', 'rating'];
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const exactKeys = (o, keys) => Object.keys(o).length === keys.length && keys.every((k) => k in o);
const intIn = (v, [min, max]) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
/** The stable issue of a value that is not a wave policy, or undefined when it is one. */
export function dispatchWavePolicyIssue(v) {
    if (!isObj(v) || !exactKeys(v, ['wave', 'ranking', 'autoPause', 'driverCancel']))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    const w = v.wave;
    if (!isObj(w) || !exactKeys(w, WAVE_KEYS))
        return 'INVALID_WAVE';
    for (const k of WAVE_KEYS)
        if (!intIn(w[k], DISPATCH_WAVE_BOUNDS[k]))
            return `INVALID_WAVE_${k.replace(/[A-Z]/g, (c) => `_${c}`).toUpperCase()}`;
    if (w.maxRadiusMeters < w.initialRadiusMeters)
        return 'INVALID_WAVE_MAX_RADIUS_BELOW_INITIAL';
    // A time budget shorter than one offer could never wait for an answer.
    if (w.maxTotalSeconds < w.offerTtlSeconds)
        return 'INVALID_WAVE_BUDGET_BELOW_OFFER_TTL';
    const r = v.ranking;
    if (!isObj(r) || !exactKeys(r, RANKING_KEYS))
        return 'INVALID_RANKING';
    const weights = r.weightsBasisPoints;
    if (!isObj(weights) || !exactKeys(weights, WEIGHT_KEYS))
        return 'INVALID_RANKING_WEIGHTS';
    for (const k of WEIGHT_KEYS)
        if (!intIn(weights[k], DISPATCH_WAVE_BOUNDS.weightBasisPoints))
            return 'INVALID_RANKING_WEIGHTS';
    const nonRating = WEIGHT_KEYS.filter((k) => k !== 'rating').reduce((sum, k) => sum + weights[k], 0);
    // REQ-DISP-003: the rating is never the sole criterion, and an all-zero ranking ranks nothing.
    if (nonRating === 0)
        return 'INVALID_RANKING_WEIGHTS_RATING_ONLY_OR_ZERO';
    if (!intIn(r.etaRefSeconds, DISPATCH_WAVE_BOUNDS.etaRefSeconds))
        return 'INVALID_RANKING_ETA_REF';
    if (!intIn(r.pickupRefMeters, DISPATCH_WAVE_BOUNDS.pickupRefMeters))
        return 'INVALID_RANKING_PICKUP_REF';
    if (!intIn(r.idleRefSeconds, DISPATCH_WAVE_BOUNDS.idleRefSeconds))
        return 'INVALID_RANKING_IDLE_REF';
    if (!intIn(r.ratingMax, DISPATCH_WAVE_BOUNDS.ratingMax))
        return 'INVALID_RANKING_RATING_MAX';
    const ap = v.autoPause;
    if (!isObj(ap) || !('ignoredOffersInARow' in ap) || Object.keys(ap).some((k) => !AUTO_PAUSE_KEYS.includes(k)) || !intIn(ap.ignoredOffersInARow, DISPATCH_WAVE_BOUNDS.ignoredOffersInARow))
        return 'INVALID_AUTO_PAUSE';
    if (('countReofferMisses' in ap && typeof ap.countReofferMisses !== 'boolean') || ('resetDaily' in ap && typeof ap.resetDaily !== 'boolean') || ('notify' in ap && typeof ap.notify !== 'boolean'))
        return 'INVALID_AUTO_PAUSE';
    if ('durationsSeconds' in ap) {
        const d = ap.durationsSeconds;
        const [minSteps, maxSteps] = DISPATCH_WAVE_BOUNDS.pauseEscalationSteps;
        if (!Array.isArray(d) || d.length < minSteps || d.length > maxSteps || !d.every((x) => intIn(x, DISPATCH_WAVE_BOUNDS.pauseDurationSeconds)))
            return 'INVALID_AUTO_PAUSE_DURATIONS';
    }
    const dc = v.driverCancel;
    if (!isObj(dc) || !exactKeys(dc, ['redispatchLimit']) || !intIn(dc.redispatchLimit, DISPATCH_WAVE_BOUNDS.redispatchLimit))
        return 'INVALID_DRIVER_CANCEL';
    return undefined;
}
export function validDispatchWavePolicyValue(v) {
    return dispatchWavePolicyIssue(v) === undefined;
}
const AUTO_PAUSE_KEYS = ['ignoredOffersInARow', 'countReofferMisses', 'durationsSeconds', 'resetDaily', 'notify'];
/** The value with every member in a fixed order (what a version stores and a reviewer approves, byte for byte). */
function normalized(v) {
    const pick = (o, keys) => Object.fromEntries(keys.map((k) => [k, o[k]]));
    return {
        wave: pick(v.wave, WAVE_KEYS),
        ranking: {
            weightsBasisPoints: pick(v.ranking.weightsBasisPoints, WEIGHT_KEYS),
            etaRefSeconds: v.ranking.etaRefSeconds,
            pickupRefMeters: v.ranking.pickupRefMeters,
            idleRefSeconds: v.ranking.idleRefSeconds,
            ratingMax: v.ranking.ratingMax,
        },
        autoPause: {
            ignoredOffersInARow: v.autoPause.ignoredOffersInARow,
            ...(v.autoPause.countReofferMisses !== undefined ? { countReofferMisses: v.autoPause.countReofferMisses } : {}),
            ...(v.autoPause.durationsSeconds !== undefined ? { durationsSeconds: [...v.autoPause.durationsSeconds] } : {}),
            ...(v.autoPause.resetDaily !== undefined ? { resetDaily: v.autoPause.resetDaily } : {}),
            ...(v.autoPause.notify !== undefined ? { notify: v.autoPause.notify } : {}),
        },
        driverCancel: { redispatchLimit: v.driverCancel.redispatchLimit },
    };
}
/** `dispatch.wavePolicy@<scope>:<ref>#v<n>`. */
export function dispatchWavePolicyVersionOf(scope, scopeRef, version) {
    return `${DISPATCH_WAVE_POLICY_KEY}@${scope}:${scopeRef}#v${version}`;
}
/** Build a valid version (country, state or market scope). Throws a RangeError on a malformed scope ref or value. */
export function dispatchWavePolicySetting(input) {
    if (!settingScopeRefValid(input.scope, input.scopeRef)) {
        throw new RangeError(`a ${input.scope}-scope wave policy needs a valid ${SCOPE_REF_KIND[input.scope]}, got ${input.scopeRef}`);
    }
    const issue = dispatchWavePolicyIssue(input.value);
    if (issue !== undefined)
        throw new RangeError(`invalid dispatch wave policy: ${issue}`);
    return policySetting({
        key: DISPATCH_WAVE_POLICY_KEY,
        scope: input.scope,
        scopeRef: input.scopeRef,
        value: normalized(input.value),
        version: input.version,
        effectiveFrom: input.effectiveFrom,
        ...(input.effectiveTo !== undefined ? { effectiveTo: input.effectiveTo } : {}),
        mode: 'OVERRIDABLE',
    });
}
/** The default decision (the pre-D444 behaviour), labelled with why it applies: nothing governs, or it cannot be read. */
export function defaultDispatchWavePolicyDecision(marketId, reasonCode) {
    return {
        marketId,
        value: DEFAULT_DISPATCH_WAVE_POLICY,
        reasonCode,
        policyVersion: `${DISPATCH_WAVE_POLICY_KEY}@${reasonCode === 'DISPATCH_WAVE_POLICY_DEFAULT' ? 'default' : 'invalid'}`,
    };
}
/**
 * The wave policy in force for `marketId` at `at`, over the GOVERNED reader's versions (market → state → country; the most
 * specific version wins; `regionCode` is the market's registered state, ADR-0124 - a market with none skips the layer). Never throws: an
 * unreadable policy resolves the default with `DISPATCH_WAVE_POLICY_INVALID`.
 */
export function resolveDispatchWavePolicy(settings, marketId, at, regionCode) {
    const fallback = (reasonCode) => defaultDispatchWavePolicyDecision(marketId, reasonCode);
    if (!MARKET_ID.test(marketId))
        return fallback('DISPATCH_WAVE_POLICY_INVALID');
    const ctx = marketSettingContext(marketId, regionCode);
    const relevant = settings.filter((s) => s.key === DISPATCH_WAVE_POLICY_KEY && (s.scope === 'country' || s.scope === 'region' || s.scope === 'market'));
    let resolution;
    try {
        resolution = resolvePolicy(relevant, ctx, DISPATCH_WAVE_POLICY_KEY, at);
    }
    catch {
        return fallback('DISPATCH_WAVE_POLICY_INVALID');
    }
    if (resolution.reason === 'NOT_SET')
        return fallback('DISPATCH_WAVE_POLICY_DEFAULT');
    if (resolution.scope === undefined || resolution.version === undefined || (resolution.scope !== 'country' && resolution.scope !== 'region' && resolution.scope !== 'market'))
        return fallback('DISPATCH_WAVE_POLICY_INVALID');
    if (!validDispatchWavePolicyValue(resolution.value))
        return fallback('DISPATCH_WAVE_POLICY_INVALID');
    const ref = settingScopeRefIn(resolution.scope, ctx) ?? '';
    const snap = settingSnapshotFields(relevant, DISPATCH_WAVE_POLICY_KEY, resolution.scope, ref, resolution.version);
    return {
        marketId,
        value: resolution.value,
        reasonCode: 'DISPATCH_WAVE_POLICY_GOVERNED',
        policyVersion: dispatchWavePolicyVersionOf(resolution.scope, ref, resolution.version),
        ...(snap.contentHash !== undefined ? { contentHash: snap.contentHash } : {}),
        ...(snap.changeId !== undefined ? { changeId: snap.changeId } : {}),
    };
}
// ---- D-43: the auto-pause after ignored offers ---------------------------------------------------------------------
/** The stable reason a driver was paused by the platform: he let the governed number of offers lapse in a row. */
export const DRIVER_AUTO_PAUSED_IGNORED_OFFERS = 'DRIVER_AUTO_PAUSED_IGNORED_OFFERS';
/** The stable reason a paused driver is not offered a ride (the wave planner's exclusion). */
export const DRIVER_DISPATCH_PAUSED = 'DRIVER_DISPATCH_PAUSED';
/**
 * How many MISSES in a row the driver let lapse, counted from the most recent offer back to his last answer (an accept or
 * a reject breaks the streak). `latestFirst` lists his ended offers since he last resumed, most recent first; offers that
 * ended for another reason (another driver won, the ride was cancelled) are not in it — they say nothing about him.
 *
 * D856 (owner R3): unless `countReofferMisses` is on, the streak counts DISTINCT rides: the offers of one ride that all lapsed
 * (the platform offered him the same ride again) are ONE miss, not one per offer. An entry with no ride id is its own ride.
 */
export function ignoredOffersInARow(latestFirst, options = {}) {
    const everyOffer = options.countReofferMisses === true;
    const rides = new Set();
    let n = 0;
    for (const entry of latestFirst) {
        const answer = typeof entry === 'string' ? entry : entry.answer;
        if (answer !== 'expired')
            break;
        const rideId = typeof entry === 'string' ? undefined : entry.rideId;
        if (everyOffer || rideId === undefined) {
            n += 1;
        }
        else if (!rides.has(rideId)) {
            rides.add(rideId);
            n += 1;
        }
    }
    return n;
}
/** Whether the policy pauses a driver whose ended offers are `latestFirst` (0 in the policy = never). */
export function autoPauseDue(latestFirst, policy) {
    const threshold = policy.autoPause.ignoredOffersInARow;
    return threshold > 0 && ignoredOffersInARow(latestFirst, { countReofferMisses: policy.autoPause.countReofferMisses === true }) >= threshold;
}
/** The stable reason a timed pause ended by itself (D856, R4). The driver's own early resume has no reason code of its own: `resumedBy` says who. */
export const DRIVER_AUTO_RESUMED_TIMER = 'DRIVER_AUTO_RESUMED_TIMER';
/**
 * D856 (owner R4): the length, in seconds, of the Nth pause of the market-local day under the policy - `durationsSeconds[N - 1]`, the last value repeating - or `undefined`
 * when the policy sets no timer (the pause then lasts until the driver resumes). `escalationNumber` counts from 1.
 */
export function autoPauseDurationSeconds(autoPause, escalationNumber) {
    const list = autoPause.durationsSeconds;
    if (list === undefined || list.length === 0)
        return undefined;
    const index = Math.min(Math.max(1, Math.trunc(escalationNumber)), list.length) - 1;
    return list[index];
}
/**
 * D856: how many ended offers a store reads per miss it needs. One ride is offered to one driver at most `wave.maxWaves` times (the policy floor: 20), so
 * `threshold` distinct rides never span more than `threshold x 20` offers.
 */
export const AUTO_PAUSE_READ_PER_MISS = DISPATCH_WAVE_BOUNDS.maxWaves[1];
// ---- D-44: the automatic re-dispatch after a driver's cancel -------------------------------------------------------
/** The stable reason a ride is searching again: its driver cancelled after accepting and the policy re-dispatched it. */
export const RIDE_REDISPATCHED_AFTER_DRIVER_CANCEL = 'RIDE_REDISPATCHED_AFTER_DRIVER_CANCEL';
/** The stable reason a driver's cancel ended the ride: the re-dispatch limit was reached (or the policy has none). */
export const RIDE_REDISPATCH_LIMIT_REACHED = 'RIDE_REDISPATCH_LIMIT_REACHED';
/** The stable reason a driver's cancel at the pickup ended the ride: a ride the driver reached is not re-dispatched. */
export const RIDE_REDISPATCH_NOT_AFTER_ARRIVAL = 'RIDE_REDISPATCH_NOT_AFTER_ARRIVAL';
/** Whether a ride already re-dispatched `redispatchCount` times may be dispatched again after its driver's cancel. */
export function redispatchAllowed(redispatchCount, policy) {
    return redispatchCount < policy.driverCancel.redispatchLimit;
}
//# sourceMappingURL=wave-policy.js.map